import { initializeApp } from 'firebase/app';
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    User,
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    sendPasswordResetEmail as firebaseSendPasswordReset
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';
import { isPlatform } from '../utils/platform';

// Firebase configuration
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
import { getStorage } from 'firebase/storage';
export const storage = getStorage(app);

// Initialize Functions (for calling Cloud Functions)
import { getFunctions } from 'firebase/functions';
export const functions = getFunctions(app, 'us-central1'); // Specify region

export { app };

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// User Profile Interface
export interface UserProfile {
    uid: string;
    email: string;
    displayName: string;
    photoURL?: string;
    createdAt: Date;
    lastLoginAt: Date;
    onboardingComplete: boolean;
    subscriptionTier?: 'free' | 'premium' | 'pro';
    // Extended Profile Details
    age?: number;
    gender?: string;
    location?: string;
    bio?: string;
    interests?: string[];
    preferences: {
        language: 'hindi' | 'english' | 'mixed';
        communicationStyle: 'formal' | 'casual';
    };
    // User-Reported Symptoms (Collected during Onboarding)
    symptomsData?: {
        symptoms: string;
        consentToStore: boolean;
        collectedAt: Date;
    };
    // AI Companion Pro - Mental Health Data
    mentalHealthData?: {
        hasExistingConcerns: boolean;
        symptoms: {
            depression: string[];
            anxiety: string[];
            sleep: string[];
            trauma: string[];
        };
        previousDiagnosis?: string;
        additionalInfo?: string;
        collectedAt?: Date;
    };
    // AI Companion Pro - User Consents
    consents?: {
        dataUsageForAI: boolean;
        proModeAccess: boolean;
        consentDate: string;
        version?: string; // Tracks which disclaimer version was accepted
    };
}


// Journal Entry Interface
export interface JournalEntry {
    entryId: string;
    userId: string;
    title: string;
    content: string;
    mood: 'very_happy' | 'happy' | 'neutral' | 'sad' | 'very_sad';
    emotions: string[];
    tags: string[];
    isPrivate: boolean;
    createdAt: Date;
    updatedAt: Date;
    aiInsights?: {
        sentimentScore?: number;
        sentimentMagnitude?: number;
        keyThemes?: string[];
        positiveMentions?: string[];
        negativeMentions?: string[];
        potentialTriggers?: string[];
        copingMentioned?: string[];
        riskFlags?: string[];
        summary?: string;
        conversationalResponse?: string;
        therapeuticPerspective?: string;
        thoughtPatterns?: string;
        emotionalNuance?: string;
        tryThis?: string;
        analysisTimestamp?: Date;
        modelVersion?: string;
        detectedLanguage?: string;
    };
}

// Firebase Service Class
export class FirebaseService {
    private currentUser: User | null = null;

    constructor() {
        // Listen for auth state changes
        onAuthStateChanged(auth, (user) => {
            this.currentUser = user;
            if (user) {
                this.updateLastLoginTime(user.uid);
            }
        });
    }

    // Sign up with email and password
    async signUp(email: string, password: string, displayName: string): Promise<UserProfile> {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Update display name
            await updateProfile(user, { displayName });

            // Create user profile in Firestore
            const userProfile: UserProfile = {
                uid: user.uid,
                email: user.email!,
                displayName,
                createdAt: new Date(),
                lastLoginAt: new Date(),
                onboardingComplete: false,
                preferences: {
                    language: 'mixed',
                    communicationStyle: 'casual'
                }
            };

            await this.createUserProfile(userProfile);
            return userProfile;
        } catch (error: any) {
            console.error('Sign up error:', error);
            throw new Error(this.getAuthErrorMessage(error.code));
        }
    }

    // Sign in with email and password
    async signIn(email: string, password: string): Promise<UserProfile> {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const userProfile = await this.getUserProfile(user.uid);
            if (!userProfile) {
                throw new Error('User profile not found');
            }

            return userProfile;
        } catch (error: any) {
            console.error('Sign in error:', error);
            throw new Error(this.getAuthErrorMessage(error.code));
        }
    }

    // Sign in with Google
    async signInWithGoogle(): Promise<UserProfile> {
        try {
            let user: User;

            if (isPlatform('hybrid')) {
                // On mobile, use redirect
                const redirectResult = await getRedirectResult(auth);

                if (redirectResult?.user) {
                    user = redirectResult.user;
                } else {
                    await signInWithRedirect(auth, googleProvider);
                    throw new Error('Redirecting to Google Sign-In...');
                }
            } else {
                // On web, use popup
                const result = await signInWithPopup(auth, googleProvider);
                user = result.user;
            }

            // Check if user profile exists
            let userProfile = await this.getUserProfile(user.uid);

            if (!userProfile) {
                // Create new profile for Google user
                userProfile = {
                    uid: user.uid,
                    email: user.email!,
                    displayName: user.displayName || 'User',
                    photoURL: user.photoURL || '',
                    createdAt: new Date(),
                    lastLoginAt: new Date(),
                    onboardingComplete: false,
                    preferences: {
                        language: 'mixed',
                        communicationStyle: 'casual'
                    }
                };

                await this.createUserProfile(userProfile);
            }

            // Always try to initialize Beta Analytics (safe, checks for existence internally)
            // This covers:
            // 1. New users (just created above)
            // 2. Existing users who missed initialization
            // 3. Returning users (safe no-op)
            try {
                const { betaAnalyticsService } = await import('./betaAnalyticsService');
                await betaAnalyticsService.initializeUser(user.uid, {
                    email: user.email!,
                    displayName: user.displayName || 'User',
                    photoURL: user.photoURL || undefined,
                    ageRange: 'unknown',
                    studentOrWorking: 'unknown',
                    primaryStressor: 'unknown',
                    preferredLanguage: 'en',
                    acquisitionSource: 'google_auth' // Will be ignored if doc exists
                });
            } catch (e) {
                console.error('Failed to ensure analytics for Google user:', e);
            }

            return userProfile;
        } catch (error: any) {
            // Start of improved error handling
            if (error.message === 'Redirecting to Google Sign-In...') {
                // This is an expected flow interruption for redirect mode
                throw error;
            }

            console.error('Google sign in error:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);

            if (error.code) {
                throw new Error(this.getAuthErrorMessage(error.code));
            } else if (error.message) {
                // Fallback to the raw error message if no code is present
                throw new Error(error.message);
            } else {
                throw new Error('An unknown error occurred during Google Sign-In');
            }
        }
    }

    // Sign out
    async signOut(): Promise<void> {
        try {
            await firebaseSignOut(auth);
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    }

    // Send password reset email
    async sendPasswordResetEmail(email: string): Promise<void> {
        try {
            await firebaseSendPasswordReset(auth, email, {
                url: window.location.origin,
                handleCodeInApp: false
            });
        } catch (error: any) {
            console.error('Password reset error:', error);
            throw new Error(this.getAuthErrorMessage(error.code));
        }
    }

    // Get current user
    getCurrentUser(): User | null {
        return this.currentUser;
    }

    // Create user profile
    async createUserProfile(userProfile: UserProfile): Promise<void> {
        try {
            await setDoc(doc(db, 'users', userProfile.uid), {
                ...userProfile,
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Error creating user profile:', error);
            throw error;
        }
    }

    // Get user profile
    async getUserProfile(uid: string): Promise<UserProfile | null> {
        try {
            const docRef = doc(db, 'users', uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const profile: UserProfile = {
                    ...data,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    lastLoginAt: data.lastLoginAt?.toDate() || new Date()
                } as UserProfile;
                return profile;
            }

            return null;
        } catch (error) {
            console.error('Error getting user profile:', error);
            throw error;
        }
    }

    // Update user profile
    async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
        try {
            const docRef = doc(db, 'users', uid);
            await updateDoc(docRef, {
                ...updates,
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    // Complete onboarding
    async completeOnboarding(uid: string, onboardingData: any): Promise<void> {
        try {
            const updates: Partial<UserProfile> = {
                ...onboardingData,
                onboardingComplete: true,
                preferences: {
                    ...onboardingData.preferences,
                    language: onboardingData.preferences?.language || 'mixed',
                    communicationStyle: onboardingData.preferences?.communicationStyle || 'casual'
                }
            };

            await this.updateUserProfile(uid, updates);
        } catch (error) {
            console.error('Error completing onboarding:', error);
            throw error;
        }
    }

    // Update last login time
    private async updateLastLoginTime(uid: string): Promise<void> {
        try {
            const docRef = doc(db, 'users', uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                await updateDoc(docRef, {
                    lastLoginAt: serverTimestamp()
                });
            } else {
                // If doc doesn't exist yet (e.g. during signup), it will be created shortly
                // We can ignore this error or try to create it if we wanted to be aggressive
            }
        } catch (error) {
            // Check if it's just a missing doc error
            console.log('Note: last login update skipped (new user?)');
        }
    }

    // Get user-friendly error messages
    private getAuthErrorMessage(errorCode: string): string {
        switch (errorCode) {
            case 'auth/email-already-in-use':
                return 'This email is already registered. Please sign in instead.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/operation-not-allowed':
                return 'Email/password accounts are not enabled. Please contact support.';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters long.';
            case 'auth/user-disabled':
                return 'This account has been disabled. Please contact support.';
            case 'auth/user-not-found':
                return 'No account found with this email. Please sign up first.';
            case 'auth/wrong-password':
                return 'Incorrect password. Please try again.';
            case 'auth/invalid-credential':
                return 'Invalid email or password. Please try again.';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later.';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection.';
            case 'auth/popup-closed-by-user':
                return 'Sign-in cancelled. Please try again.';
            case 'auth/popup-blocked':
                return 'Pop-up blocked. Please allow pop-ups for this site.';
            case 'auth/cancelled-popup-request':
                return 'The sign-in popup was cancelled. Please try again.';
            case 'auth/unauthorized-domain':
                return 'This domain is not authorized for Google Sign-In.';
            default:
                return 'An error occurred. Please try again.';
        }
    }

    // ==================== JOURNAL MANAGEMENT ====================

    // Create journal entry
    async createJournalEntry(entry: Omit<JournalEntry, 'entryId'>): Promise<string> {
        try {
            const { collection, addDoc } = await import('firebase/firestore');
            const docRef = await addDoc(collection(db, 'journal_entries'), {
                ...entry,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            return docRef.id;
        } catch (error) {
            console.error('Error creating journal entry:', error);
            throw error;
        }
    }

    // Get journal entries for a user
    async getJournalEntries(userId: string, limit: number = 20): Promise<JournalEntry[]> {
        try {
            const { collection, query, where, orderBy, getDocs, limit: limitQuery } = await import('firebase/firestore');
            const q = query(
                collection(db, 'journal_entries'),
                where('userId', '==', userId),
                orderBy('createdAt', 'desc'),
                limitQuery(limit)
            );

            const querySnapshot = await getDocs(q);
            const entries: JournalEntry[] = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                entries.push({
                    entryId: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    updatedAt: data.updatedAt?.toDate() || new Date(),
                    aiInsights: data.aiInsights ? {
                        ...data.aiInsights,
                        analysisTimestamp: data.aiInsights.analysisTimestamp?.toDate()
                    } : undefined
                } as JournalEntry);
            });

            return entries;
        } catch (error) {
            console.error('Error getting journal entries:', error);
            throw error;
        }
    }

    // Listen to journal entries in real-time
    listenToJournalEntries(
        userId: string,
        onUpdate: (entries: JournalEntry[]) => void,
        onError: (error: Error) => void,
        limitCount: number = 20
    ): () => void {
        // Import needed Firestore functions (can't use dynamic import in non-async function)
        import('firebase/firestore').then(({ collection, query, where, orderBy, onSnapshot, limit: limitQuery }) => {
            const q = query(
                collection(db, 'journal_entries'),
                where('userId', '==', userId),
                orderBy('createdAt', 'desc'),
                limitQuery(limitCount)
            );

            const unsubscribe = onSnapshot(
                q,
                (querySnapshot) => {
                    const entries: JournalEntry[] = [];
                    querySnapshot.forEach((doc) => {
                        const data = doc.data();
                        entries.push({
                            entryId: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate() || new Date(),
                            updatedAt: data.updatedAt?.toDate() || new Date(),
                            aiInsights: data.aiInsights ? {
                                ...data.aiInsights,
                                analysisTimestamp: data.aiInsights.analysisTimestamp?.toDate()
                            } : undefined
                        } as JournalEntry);
                    });
                    onUpdate(entries);
                },
                (error) => {
                    console.error('Error listening to journal entries:', error);
                    onError(error as Error);
                }
            );

            // Store unsubscribe function
            (this as any).journalUnsubscribe = unsubscribe;
        }).catch((error) => {
            console.error('Error setting up journal listener:', error);
            onError(error as Error);
        });

        // Return unsubscribe function
        return () => {
            if ((this as any).journalUnsubscribe) {
                (this as any).journalUnsubscribe();
            }
        };
    }

    // Update journal entry
    async updateJournalEntry(entryId: string, updates: Partial<JournalEntry>): Promise<void> {
        try {
            const docRef = doc(db, 'journal_entries', entryId);
            await updateDoc(docRef, {
                ...updates,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Error updating journal entry:', error);
            throw error;
        }
    }

    // Delete journal entry
    async deleteJournalEntry(entryId: string): Promise<void> {
        try {
            const { deleteDoc } = await import('firebase/firestore');
            const docRef = doc(db, 'journal_entries', entryId);
            await deleteDoc(docRef);
        } catch (error) {
            console.error('Error deleting journal entry:', error);
            throw error;
        }
    }

    // Create journal revision (save old version before edit)
    async createJournalRevision(entryId: string, revisionData: any): Promise<string> {
        try {
            const { collection, addDoc } = await import('firebase/firestore');
            const docRef = await addDoc(
                collection(db, 'journal_entries', entryId, 'revisions'),
                {
                    ...revisionData,
                    savedAt: serverTimestamp()
                }
            );
            return docRef.id;
        } catch (error) {
            console.error('Error creating revision:', error);
            throw error;
        }
    }

    // Get revisions for an entry
    async getJournalRevisions(entryId: string): Promise<any[]> {
        try {
            const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
            const q = query(
                collection(db, 'journal_entries', entryId, 'revisions'),
                orderBy('savedAt', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const revisions: any[] = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                revisions.push({
                    revisionId: doc.id,
                    ...data,
                    savedAt: data.savedAt?.toDate() || new Date(),
                    createdAt: data.createdAt?.toDate() || new Date(),
                    editedAt: data.editedAt?.toDate() || new Date(),
                });
            });

            return revisions;
        } catch (error) {
            console.error('Error getting revisions:', error);
            throw error;
        }
    }

    // ====================================
    // CHAT METHODS
    // ====================================

    // Call the chat AI function to get a response
    async getChatResponse(userMessage: string, userId: string, conversationHistory: any[] = [], language: string = 'english'): Promise<string> {
        try {
            const { httpsCallable } = await import('firebase/functions');
            const { functions } = await import('./firebaseService');

            const generateResponse = httpsCallable(functions, 'generateEmpathicResponse');
            const result: any = await generateResponse({
                userMessage,
                userId,
                previousMessages: conversationHistory.slice(-5), // Last 5 messages for context
                userProfile: {
                    language: language
                }
            });

            // The function returns a TherapeuticResponse object with a 'message' field
            return result.data.message || result.data.response || result.data;
        } catch (error) {
            console.error('Error getting chat response:', error);
            throw error;
        }
    }

    // Helper to determine collection based on session ID
    private getCollectionForSession(sessionId: string): string {
        return sessionId.startsWith('pro_') ? 'pro_chat_sessions' : 'chat_sessions';
    }

    // Create or update chat session
    async saveChatSession(sessionId: string, messages: any[], userId: string): Promise<void> {
        try {
            const { doc, setDoc } = await import('firebase/firestore');
            const collectionName = this.getCollectionForSession(sessionId);

            await setDoc(doc(db, collectionName, sessionId), {
                userId,
                messages,
                lastMessageAt: new Date(),
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error('Error saving chat session:', error);
            throw error;
        }
    }

    // Get chat session
    async getChatSession(sessionId: string): Promise<any | null> {
        try {
            const { doc, getDoc } = await import('firebase/firestore');
            const collectionName = this.getCollectionForSession(sessionId);
            const docSnap = await getDoc(doc(db, collectionName, sessionId));

            if (docSnap.exists()) {
                return {
                    sessionId: docSnap.id,
                    ...docSnap.data()
                };
            }
            return null;
        } catch (error) {
            console.error('Error getting chat session:', error);
            throw error;
        }
    }

    // Get Standard chat sessions (from chat_sessions)
    async getStandardSessions(userId: string, limit: number = 10): Promise<any[]> {
        return this.getSessionsFromCollection(userId, 'chat_sessions', limit);
    }

    // Get Pro chat sessions (from pro_chat_sessions)
    async getProSessions(userId: string, limit: number = 10): Promise<any[]> {
        return this.getSessionsFromCollection(userId, 'pro_chat_sessions', limit);
    }

    // Helper to get sessions from specific collection
    private async getSessionsFromCollection(userId: string, collectionName: string, limit: number = 10): Promise<any[]> {
        try {
            const { collection, query, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');

            const q = query(
                collection(db, collectionName),
                where('userId', '==', userId),
                orderBy('lastMessageAt', 'desc'),
                firestoreLimit(limit)
            );

            const querySnapshot = await getDocs(q);
            const sessions: any[] = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                sessions.push({
                    sessionId: doc.id,
                    ...data,
                    lastMessageAt: data.lastMessageAt?.toDate() || new Date()
                });
            });

            return sessions;
        } catch (error) {
            console.error(`Error getting sessions from ${collectionName}:`, error);
            return []; // Return empty array instead of throwing to prevent UI crash
        }
    }

    /**
     * @deprecated Use getStandardSessions or getProSessions instead
     */
    async getChatSessions(userId: string, limit: number = 10): Promise<any[]> {
        return this.getStandardSessions(userId, limit);
    }
    // Delete chat session
    async deleteChatSession(sessionId: string): Promise<void> {
        try {
            const { deleteDoc, doc } = await import('firebase/firestore');
            const collectionName = this.getCollectionForSession(sessionId);
            await deleteDoc(doc(db, collectionName, sessionId));
        } catch (error) {
            console.error('Error deleting chat session:', error);
            throw error;
        }
    }

    // Rename chat session
    async renameChatSession(sessionId: string, newTitle: string): Promise<void> {
        try {
            const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
            const collectionName = this.getCollectionForSession(sessionId);
            await updateDoc(doc(db, collectionName, sessionId), {
                title: newTitle,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Error renaming chat session:', error);
            throw error;
        }
    }

    // ====================================
    // DASHBOARD / STATS METHODS
    // ====================================

    // Get Check-in history
    async getCheckInHistory(userId: string, limitCount: number = 7): Promise<any[]> {
        try {
            const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
            const q = query(
                collection(db, 'users', userId, 'checkins'),
                orderBy('timestamp', 'desc'), // Assuming timestamp exists, otherwise use date
                limit(limitCount)
            );

            const querySnapshot = await getDocs(q);
            const checkins: any[] = [];
            querySnapshot.forEach((doc) => {
                checkins.push({ id: doc.id, ...doc.data() });
            });
            return checkins; // Returns Newest -> Oldest (Standard)
        } catch (error) {
            console.error('Error getting check-ins:', error);
            return [];
        }
    }

    // Update check-in (e.g. for saving AI analysis)
    async updateCheckIn(userId: string, checkInId: string, updates: any): Promise<void> {
        try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const docRef = doc(db, 'users', userId, 'checkins', checkInId);
            await updateDoc(docRef, updates);
        } catch (error) {
            console.error('Error updating check-in:', error);
            throw error;
        }
    }

    // Upload profile image
    async uploadProfileImage(userId: string, file: File): Promise<string> {
        try {
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('./firebaseService');

            // Create a reference to 'profile_images/userId/filename'
            const fileRef = ref(storage, `profile_images/${userId}/${Date.now()}_${file.name}`);

            // Upload the file
            await uploadBytes(fileRef, file);

            // Get the download URL
            const photoURL = await getDownloadURL(fileRef);
            return photoURL;
        } catch (error) {
            console.error("Error uploading profile image:", error);
            throw error;
        }
    }
}

// Export singleton instance
export const firebaseService = new FirebaseService();
